import { useEffect, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';

import MessagesService from 'src/services/MessagesService';
import useMessageStore from 'src/stores/messages';

import type { InfiniteData } from 'react-query';
import type { IMessage, IMessagesRequest, IMessagesResponse } from 'src/types/Message';

const PAGE_SIZE = 20;
export const FEED_CHECK_INTERVAL = 30_000;

type Request = Omit<IMessagesRequest, 'limit' | 'offset'>;
type ApplyOperation = { scope: string; firstId?: number };

const messageTime = (message: IMessage) => Date.parse(message.date) || 0;
const streamMessages = (pages: IMessagesResponse[]) =>
    Array.from(
        new Map(
            pages
                .flatMap((page) => page.results)
                .filter((item) => item.source === 'stream')
                .map((item) => [item.id, item])
        ).values()
    );

// The rendered list is a reading snapshot. Query pages may refresh independently of it.
export default function useBufferedFeed(scope: string, request: Request, deleting = false) {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();
    const activeScope = useRef(scope);
    const initialized = useRef(false);
    const mounted = useRef(false);
    const operation = useRef<ApplyOperation | null>(null);
    const [readyScope, setReadyScope] = useState<string | null>(null);
    const [pendingScope, setPendingScope] = useState<string | null>(null);
    const [applyingScope, setApplyingScope] = useState<string | null>(null);

    if (activeScope.current !== scope) {
        activeScope.current = scope;
        initialized.current = false;
    }

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    useEffect(() => {
        useMessageStore.getState().clear();
    }, [scope]);

    const synchronize = (data: InfiniteData<IMessagesResponse>, apply = false) => {
        if (!mounted.current || activeScope.current !== scope) return;
        if (operation.current?.scope === scope && !apply) return;
        const incoming = streamMessages(data.pages);
        const count = data.pages[0]?.count || 0;
        const current = useMessageStore.getState().messages;
        const currentById = new Map(current.map((item) => [item.id, item]));
        const unknown = incoming.filter((item) => !currentById.has(item.id));

        if (deleting) {
            useMessageStore.setState({ count });
            return;
        }

        if (!initialized.current || apply) {
            // Preserve local feedback/favorites and previously loaded cards while rebasing query pages.
            const merged = new Map(incoming.map((item) => [item.id, currentById.get(item.id) || item]));
            current.forEach((item) => {
                if (!merged.has(item.id)) merged.set(item.id, item);
            });
            useMessageStore.setState({
                messages: Array.from(merged.values()).sort((a, b) => messageTime(b) - messageTime(a)),
                count,
            });
            if (apply && operation.current) operation.current.firstId = unknown[0]?.id;
            initialized.current = true;
            setReadyScope(scope);
            setPendingScope(null);
        } else {
            // Pagination may append older messages, but cannot insert into the current reading range.
            const oldest = current.length ? Math.min(...current.map(messageTime)) : -Infinity;
            const older = unknown.filter((item) => messageTime(item) < oldest);
            if (unknown.length > older.length) setPendingScope(scope);
            useMessageStore.setState({ messages: older.length ? [...current, ...older] : current, count });
        }

        // Seed the cheap head check; do not immediately duplicate a successful main request.
        queryClient.setQueryData(['check-new-leads', scope], data.pages[0]);
    };

    const feed = useInfiniteQuery({
        queryKey: ['fetch-message', scope],
        queryFn: ({ pageParam = 0 }) =>
            MessagesService.fetchMessages({ ...request, limit: PAGE_SIZE, offset: pageParam }),
        getNextPageParam: (lastPage, pages) => (lastPage.next ? pages.length * PAGE_SIZE : false),
        onSuccess: synchronize,
        onError: () => {
            if (operation.current?.scope !== scope) {
                enqueueSnackbar('Не удалось загрузить лиды. Попробуйте ещё раз', { variant: 'error' });
            }
        },
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: 2,
        retryDelay: 1000,
    });

    useQuery({
        queryKey: ['check-new-leads', scope],
        queryFn: () => MessagesService.fetchMessages({ ...request, limit: PAGE_SIZE, offset: 0 }),
        enabled:
            readyScope === scope && initialized.current && !feed.isFetching && applyingScope !== scope && !deleting,
        staleTime: FEED_CHECK_INTERVAL,
        refetchInterval: FEED_CHECK_INTERVAL,
        refetchIntervalInBackground: false,
        cacheTime: 0,
        retry: 1,
        onSuccess: (data) => {
            if (activeScope.current !== scope || !initialized.current) return;
            const visibleIds = new Set(useMessageStore.getState().messages.map((item) => item.id));
            if (data.results.some((item) => item.source === 'stream' && !visibleIds.has(item.id)))
                setPendingScope(scope);
        },
        // A failed background check must neither erase the feed nor invent an arrival notification.
        onError: () => {},
    });

    const showNew = async () => {
        if (operation.current?.scope === scope || deleting) return;
        const next: ApplyOperation = { scope };
        const original = useMessageStore.getState().messages;
        const originalIds = new Set(original.map((item) => item.id));
        const newestOriginal = original.length ? Math.max(...original.map(messageTime)) : -Infinity;
        operation.current = next;
        setApplyingScope(scope);
        try {
            await queryClient.cancelQueries(['check-new-leads', scope], { exact: true });
            await queryClient.cancelQueries(['fetch-message', scope], { exact: true });
            if (activeScope.current !== scope || !mounted.current) return;
            // Re-fetch the loaded page range, rather than splicing a probe page into stale offsets.
            let result = await feed.refetch({ throwOnError: true });
            // If many leads arrived, bridge the gap to the old reading snapshot before revealing it.
            // Bound the work per click; a retry retains the expanded query range.
            for (let page = 0; ; page += 1) {
                if (activeScope.current !== scope || !mounted.current) return;
                if (!result.data || result.isError) throw new Error('Feed refresh failed');
                const received = streamMessages(result.data.pages);
                const tail = received[received.length - 1];
                const connected =
                    !original.length ||
                    !result.data.pages[result.data.pages.length - 1]?.next ||
                    received.some((item) => originalIds.has(item.id)) ||
                    (tail && messageTime(tail) < newestOriginal);
                if (connected) break;
                if (page >= 20 || !result.data.pages[result.data.pages.length - 1]?.results.length) {
                    throw new Error('Feed refresh range is incomplete');
                }
                result = await feed.fetchNextPage({ throwOnError: true });
            }
            synchronize(result.data, true);
            if (activeScope.current === scope && mounted.current) return next.firstId;
        } catch (_error) {
            if (activeScope.current === scope && mounted.current) {
                enqueueSnackbar('Не удалось обновить ленту. Попробуйте ещё раз', { variant: 'error' });
            }
        } finally {
            if (operation.current === next) {
                operation.current = null;
                if (mounted.current) setApplyingScope(null);
            }
        }
    };

    return {
        ...feed,
        hasNew: pendingScope === scope && readyScope === scope && initialized.current,
        isApplying: applyingScope === scope,
        showNew,
    };
}
